import { createContext, type ReactNode, useMemo } from 'react';

import { useRequiredContext } from 'hooks';
import { useSimilarProducts } from 'hooks/data-fetching';
import type { ProductId } from 'models/product';
import type { ProductCard } from 'models/productCard';
import { empty } from 'utils/helpers';

interface SimilarProductsContextType {
	hasNextPage?: boolean;
	isLoading: boolean;
	isLoadingMore?: boolean;
	items: ProductCard[];
	loadMore: () => void;
}

export const SimilarProductsContext = createContext<
	SimilarProductsContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
	productId: ProductId;
}
export const SIMILAR_PRODUCTS_PAGE_SIZE = 6;
export function SimilarProductsContextProvider({ children, productId }: Props) {
	const { hasNextPage, isLoading, isLoadingMore, items, loadMore } =
		useSimilarProducts({
			productId,
			pageSize: SIMILAR_PRODUCTS_PAGE_SIZE,
			isActive: true,
		});
	const value = useMemo(
		() => ({
			loadMore,
			isLoading,
			items: items ?? empty.array,
			isLoadingMore,
			hasNextPage,
		}),
		[hasNextPage, isLoading, isLoadingMore, items, loadMore],
	);
	return (
		<SimilarProductsContext value={value}>{children}</SimilarProductsContext>
	);
}
SimilarProductsContextProvider.displayName = 'SimilarProductsContextProvider';

export function useSimilarProductsContext() {
	return useRequiredContext(SimilarProductsContext, 'SimilarProductsContext');
}
